// Regras puras do card "Minha assinatura" e da lista de cobranças do
// Financeiro (sem React, sem rede). Textos resolvidos na chamada, no idioma
// atual. O backend (GerenciaAssinaturaService) é quem decide; aqui só se
// antecipa ao usuário o que vai acontecer.
import moment from "moment";

import { i18n } from "../../translate/i18n";
import { parseAmount } from "../PlansManager/planPriceRules";
import { round2 } from "../SubscriptionCheckout/checkoutRules";

const CARTOES = ["CARD_RECURRING", "CARD_SAVED"];
const ehCartao = (method) => CARTOES.includes(method);

const dia = (date) => moment(date).format("DD/MM/YYYY");

export const statusLabel = (subscription) => {
  switch (subscription?.status) {
    case "ACTIVE":
      return i18n.t("subscription.card.status.active");
    case "PAST_DUE":
      return i18n.t("subscription.card.status.pastDue");
    case "CANCELED":
      return i18n.t("subscription.card.status.canceled", {
        date: moment(subscription.cycleEnd).format("DD/MM"),
      });
    case "PENDING":
      return i18n.t("subscription.card.status.pending");
    default:
      return "";
  }
};

export const statusTone = (subscription) =>
  ({ ACTIVE: "ok", PAST_DUE: "error", PENDING: "warn" }[subscription?.status] ||
  "muted");

// Cartão anual (CARD_SAVED) só renova sozinho se o cartão ficou salvo no
// MP, o que o front não enxerga: o texto promete só o aviso.
export const renovacaoLabel = (subscription) => {
  if (!subscription) return "";
  if (subscription.canceledAt || subscription.status === "CANCELED") {
    return i18n.t("subscription.card.renewal.none");
  }
  if (subscription.paymentMethod === "PIX") {
    return i18n.t("subscription.card.renewal.pix");
  }
  if (subscription.paymentMethod === "CARD_SAVED") {
    return i18n.t("subscription.card.renewal.savedCard");
  }
  return i18n.t("subscription.card.renewal.auto");
};

// Upgrade aguardando pagamento: o backend já grava `pendingPlanId` com a
// fatura UPGRADE aberta. Não é troca agendada para o fim do ciclo — o plano
// novo vale quando a fatura for paga.
export const upgradePendente = (subscription, openInvoice) =>
  ["ACTIVE", "PAST_DUE"].includes(subscription?.status) &&
  openInvoice?.kind === "UPGRADE" &&
  openInvoice?.status !== "paid" &&
  openInvoice?.status !== "canceled";

// Com upgrade pendente, trocar plano/período é recusado pelo backend
// (ERR_SUB_UPGRADE_PENDING): os botões somem até a fatura ser paga.
export const acoesDisponiveis = (subscription, openInvoice) => {
  if (!["ACTIVE", "PAST_DUE"].includes(subscription?.status)) return [];
  const acoes = upgradePendente(subscription, openInvoice)
    ? []
    : ["changePlan", "changePeriod"];
  if (ehCartao(subscription.paymentMethod)) acoes.push("changeCard");
  acoes.push("cancel");
  return acoes;
};

// Troca de período vale na virada do ciclo. No cartão ela muda o meio
// (mensal recorrente <-> anual avulso): o cron gera a fatura sem meio e a
// primeira cobrança do período novo é feita pelo checkout.
export const avisoTrocaPeriodo = (subscription, novoPeriodo) => {
  if (!subscription || !novoPeriodo) return null;
  if (novoPeriodo === subscription.period) {
    return subscription.pendingPeriod
      ? i18n.t("subscription.manage.periodUndo")
      : null;
  }
  const date = dia(subscription.cycleEnd);
  if (subscription.paymentMethod === "PIX") {
    return i18n.t("subscription.manage.periodNoticePix", { date });
  }
  return i18n.t(
    novoPeriodo === "ANUAL"
      ? "subscription.manage.periodNoticeCardYearly"
      : "subscription.manage.periodNoticeCardMonthly",
    { date }
  );
};

export const avisoTrocaPlano = (precoAtual, precoNovo) => {
  const atual = round2(Number(precoAtual) || 0);
  const novo = round2(Number(precoNovo) || 0);
  if (novo > atual) return { tipo: "upgrade" };
  if (novo < atual) return { tipo: "downgrade" };
  return { tipo: "same" };
};

export const periodLabel = (period) =>
  period === "ANUAL"
    ? i18n.t("subscription.checkout.yearly")
    : i18n.t("subscription.checkout.monthly");

export const periodicidadeLabel = (subscription) => {
  if (!subscription) return "";
  const periodo = periodLabel(subscription.period);
  const method = subscription.paymentMethod;
  let meio = "";
  if (method === "PIX") meio = i18n.t("subscription.card.methodPix");
  else if (method === "CARD_RECURRING") meio = i18n.t("subscription.card.methodRecurring");
  else if (method === "CARD_SAVED") {
    const n = Number(subscription.installments) || 1;
    meio =
      n > 1
        ? i18n.t("subscription.card.methodInstallments", { n })
        : i18n.t("subscription.card.methodCardOnce");
  }
  return meio ? `${periodo} · ${meio}` : periodo;
};

export const precoNoPeriodo = (plan, period) => {
  if (!plan) return 0;
  return parseAmount(period === "ANUAL" ? plan.amountYearly : plan.amount);
};

// Próxima cobrança: a fatura aberta, se já existe; senão o fim do ciclo com
// o preço do período que vale no ciclo seguinte (`prices` já é do plano do
// próximo ciclo). Desconto Pix só quando o meio continua Pix.
export const proximaCobranca = ({
  subscription,
  openInvoice,
  prices,
  pixDiscountPercent,
}) => {
  if (!subscription || subscription.status === "CANCELED" || subscription.canceledAt) {
    return null;
  }
  // Fatura UPGRADE aberta não é a cobrança do ciclo (o card avisa à parte).
  if (openInvoice && openInvoice.kind !== "UPGRADE") {
    return { date: dia(openInvoice.dueDate), value: Number(openInvoice.value) || 0 };
  }
  if (!subscription.cycleEnd) return null;
  const period = subscription.pendingPeriod || subscription.period || "MENSAL";
  const bruto = Number(prices?.[period]) || 0;
  if (!bruto) return null;
  const mudaMeio = period !== subscription.period;
  const value =
    subscription.paymentMethod === "PIX" && !mudaMeio
      ? round2(bruto - round2((bruto * Number(pixDiscountPercent || 0)) / 100))
      : bruto;
  return { date: dia(subscription.cycleEnd), value };
};

const BANDEIRAS = {
  master: "Mastercard",
  visa: "Visa",
  amex: "Amex",
  elo: "Elo",
  hipercard: "Hipercard",
  diners: "Diners",
  cabal: "Cabal",
};

// O MP devolve o payment_method_id ("master", "debvisa"...). Nome de marca
// não se traduz.
export const brandLabel = (brand) => {
  if (!brand) return "";
  const key = String(brand).toLowerCase().replace(/^deb/, "");
  if (BANDEIRAS[key]) return BANDEIRAS[key];
  return key.charAt(0).toUpperCase() + key.slice(1);
};

export const statusFatura = (invoice, hoje = new Date()) => {
  if (invoice?.status === "paid") return "paid";
  if (invoice?.status === "canceled") return "canceled";
  const dias = moment(invoice?.dueDate)
    .startOf("day")
    .diff(moment(hoje).startOf("day"), "days");
  return dias < 0 ? "overdue" : "open";
};

export const periodoFaturaLabel = (invoice) =>
  invoice?.period ? periodLabel(invoice.period) : "—";

export const meioFaturaLabel = (invoice) => {
  const method = invoice?.paymentMethod;
  if (method === "PIX") return i18n.t("subscription.card.methodPix");
  if (ehCartao(method)) return i18n.t("subscription.checkout.tabCard");
  return "—";
};

// "Já paguei" no card: a contratação aguardando confirmação ou qualquer
// fatura aberta da assinatura (Pix pago, cartão aprovado) pode ter o
// pagamento confirmado no Mercado Pago com o webhook perdido. Cancelada não.
export const podeVerificarPagamento = (subscription, openInvoice) => {
  if (!subscription || subscription.status === "CANCELED") return false;
  return subscription.status === "PENDING" || !!openInvoice;
};
